import url from '@/utils/config';
import axios, { AxiosResponse } from 'axios';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Sha256 } from '@aws-crypto/sha256-universal';
// eslint-disable-next-line import/no-extraneous-dependencies
import { HttpRequest } from '@smithy/protocol-http';
// eslint-disable-next-line import/no-extraneous-dependencies
import { LoginResponse } from '@/types/api/mypage/login';
import { ApiUrl } from '@/utils/constants';
// eslint-disable-next-line import/no-extraneous-dependencies
import { SignatureV4 } from '@smithy/signature-v4';

// 30分経過した場合、クレデンシャル情報を更新する
const SESSION_TIME_LIMIT_MINUTE = 30;

/**
 * 現在時刻を取得する
 * ※形式：YYYYMMDDHHMM
 *
 * @returns {string} : 現在日時
 */
const getCurrentTime = () => {
  const date = new Date();
  date.setHours(date.getHours() + 9);
  return date.toISOString().replace(/[^0-9]/g, '').slice(0, -5);
};

/**
 * セッション開始時間を設定する
 * ※形式：YYYYMMDDHHMM
 */
const setSessionStartTime = () => {
  sessionStorage.setItem('sessionStartTime', getCurrentTime());
};
export { setSessionStartTime };

/**
 * セッション開始時間を取得する
 *
 * @returns {string | null} : セッション開始時間
 */
const getSessionStartTime = (): string | null => {
  return sessionStorage.getItem('sessionStartTime');
};

/**
 * ログインしてから指定の時間を経過したかどうか
 *
 * @returns {boolean} : ログインしてから指定の時間を経過したか
 */
const isRenewed = (): boolean => {
  const sessionStartTime = getSessionStartTime();
  if (!sessionStartTime) {
    return false;
  }
  const sessionStartTimeDate = new Date(Number(sessionStartTime.slice(0, 4)), Number(sessionStartTime.slice(4, 6)) - 1, Number(sessionStartTime.slice(6, 8)), Number(sessionStartTime.slice(8, 10)), Number(sessionStartTime.slice(10, 12)));
  sessionStartTimeDate.setHours(sessionStartTimeDate.getHours() + 9);
  sessionStartTimeDate.setMinutes(sessionStartTimeDate.getMinutes() + SESSION_TIME_LIMIT_MINUTE);

  // セッション開始時間に指定の時刻を足した時間
  const sessionStartTimeNum = Number(sessionStartTimeDate.toISOString().replace(/[^0-9]/g, '').slice(0, -5));
  const currentTimeNum = Number(getCurrentTime());
  return currentTimeNum > sessionStartTimeNum;
};

/**
 * Sigv4署名ヘッダ
 */
export type SigV4Headers = {
  headers: SigV4HeadersContent | {};
};

/**
 * Sigv4署名ヘッダ内容
 */
export type SigV4HeadersContent = {
  'x-amz-date': string;
  'x-amz-security-token': string;
  authorization: string;
  'x-amz-content-sha256': string;
};

/**
 * クレデンシャル情報を設定する
 *
 * @param {LoginResponse} credentials クレデンシャル情報
 */
const setCredentials = (credentials: LoginResponse) => {
  sessionStorage.setItem('credentials', JSON.stringify(credentials));
};
export { setCredentials };

/**
 * クレデンシャル情報を取得する
 *
 * @returns {string | null} : クレデンシャル情報
 */
const getCredentials = (): string | null => {
  return sessionStorage.getItem('credentials');
};
export { getCredentials };

/**
 * ドメイン情報を取得する
 *
 * @param {string} apiUrl URL
 * @returns {string | undefined} : ドメイン情報
 */
const getDomain = (apiUrl: string): string | undefined => {
  if (apiUrl === ApiUrl.Authn.LOGOUT || apiUrl === ApiUrl.Authn.RENEW) {
    return process.env.NEXT_PUBLIC_AUTH_URL;
  }
  return ApiUrl.DOMAIN;
};

/**
 * Sigv4署名を取得する
 *
 * @param {any} credentialsJson クレデンシャル情報(JSON)
 * @returns {SignatureV4} : Sigv4署名
 */
const getSignatureV4 = (credentialsJson: any): SignatureV4 => {
  return new SignatureV4({
    service: 'execute-api',
    region: 'ap-northeast-1',
    credentials: {
      accessKeyId: credentialsJson.credentials.access_key_id,
      secretAccessKey: credentialsJson.credentials.access_secret,
      sessionToken: credentialsJson.credentials.session_token
    },
    sha256: Sha256
  });
};

/**
 * HTTPリクエストを取得する
 *
 * @param {string} methodName メソッド
 * @param {URL} urlObj URLオブジェクト
 * @param {any} bodyObj ボディ
 * @returns {HttpRequest} : HTTPリクエスト
 */
const getHttpRequest = (methodName: string, urlObj: URL, bodyObj?: any): HttpRequest => {
  const httpRequest: {
    [prop: string]: any;
  } = {
    headers: {
      host: urlObj.hostname
    },
    hostname: urlObj.hostname,
    method: methodName,
    path: urlObj.pathname
  };
  if ((methodName === 'GET' || methodName === 'DELETE') && urlObj.search) {
    // クエリパラメータが存在する場合、署名に入れる必要がある
    httpRequest.query = Object.fromEntries(new URLSearchParams(urlObj.search.slice(1)));
  }
  if (methodName === 'POST' || methodName === 'PUT') {
    // ボディが存在する場合、署名に入れる必要がある
    httpRequest.body = JSON.stringify(bodyObj);
  }
  return new HttpRequest(httpRequest);
};

/**
 * Sigv4署名ヘッダを取得する
 *
 * @param {any} signedRequest 署名リクエスト
 * @returns {SigV4Headers} : Sigv4署名ヘッダ
 */
const getSigV4Headers = (signedRequest: any): SigV4Headers => {
  return {
    headers: {
      'x-amz-date': signedRequest.headers['x-amz-date'],
      'x-amz-security-token': signedRequest.headers['x-amz-security-token'],
      authorization: signedRequest.headers.authorization,
      'x-amz-content-sha256': signedRequest.headers['x-amz-content-sha256']
    }
  };
};

/**
 * クレデンシャル更新APIを実行する
 *
 * @returns {Promise<void>} : クレデンシャル更新API
 */
const renew = async (): Promise<void> => {
  const headers = await createSigV4Headers('GET', ApiUrl.Authn.RENEW);
  return axios.get(`${process.env.NEXT_PUBLIC_AUTH_URL}${ApiUrl.Authn.RENEW}`, headers).then((res: AxiosResponse<LoginResponse>) => {
    setCredentials(res.data);

    // セッション開始時間も更新する
    setSessionStartTime();
  }).catch(error => {
    if (isExpiredToken(error)) {
      sessionStorage.clear();
      window.location.href = url('/account/login');
    }
  }).finally(() => {});
};

/**
 * Sigv4署名ヘッダを取得する
 * ※GETメソッド用
 *
 * @param {string} apiUrl APIのURL
 * @return Sigv4署名ヘッダ
 */
const getSigV4HeadersForGet = async (apiUrl: string): Promise<SigV4Headers> => {
  // セッション時間が指定の時間を経過した場合は、クレデンシャル更新APIを実行する
  if (isRenewed()) {
    await renew();
  }
  return createSigV4Headers('GET', apiUrl);
};
export { getSigV4HeadersForGet };

/**
 * Sigv4署名ヘッダを取得する
 * ※POSTメソッド用
 *
 * @param {string} apiUrl APIのURL
 * @param {{}} params パラメータ ※パラメータの情報を署名に入れる必要がある
 * @return Sigv4署名ヘッダ
 */
const getSigV4HeadersForPost = async (apiUrl: string, params: {}): Promise<SigV4Headers> => {
  // セッション時間が指定の時間を経過した場合は、クレデンシャル更新APIを実行する
  if (isRenewed()) {
    await renew();
  }
  return createSigV4Headers('POST', apiUrl, params);
};
export { getSigV4HeadersForPost };

/**
 * Sigv4署名ヘッダを取得する
 * ※PUTメソッド
 *
 * @param {string} apiUrl APIのURL
 * @param {{}} params パラメータ ※パラメータの情報を署名に組み込む必要あり
 * @return Sigv4署名ヘッダ
 */
const getSigV4HeadersForPut = async (apiUrl: string, params: {}): Promise<SigV4Headers> => {
  // セッション時間が指定の時間を経過した場合は、クレデンシャル更新APIを実行する
  if (isRenewed()) {
    await renew();
  }
  return createSigV4Headers('PUT', apiUrl, params);
};
export { getSigV4HeadersForPut };

/**
 * Sigv4署名ヘッダを取得する
 * ※DELETEメソッド
 *
 * @param {string} apiUrl APIのURL
 * @return Sigv4署名ヘッダ
 */
const getSigV4HeadersForDelete = async (apiUrl: string): Promise<SigV4Headers> => {
  // セッション時間が指定の時間を経過した場合は、クレデンシャル更新APIを実行する
  if (isRenewed()) {
    await renew();
  }
  return createSigV4Headers('DELETE', apiUrl);
};
export { getSigV4HeadersForDelete };

/**
 * Sigv4署名ヘッダを作成する
 * ※共通処理
 *
 * API GatewayのIAM認証を通過するのに署名情報が必要
 *
 * @param {string} method メソッド
 * @param {string} apiUrl APIのURL
 * @param {{}} params パラメータ ※パラメータの情報を署名に組み込む必要あり
 * @return Sigv4署名ヘッダ
 * @href https://docs.aws.amazon.com/ja_jp/general/latest/gr/sigv4_signing.html
 */
const createSigV4Headers = async (method: string, apiUrl: string, params?: {}): Promise<SigV4Headers> => {
  // ストレージのクレデンシャル情報を取得する
  const credentials = getCredentials();
  if (!credentials) return {
    headers: {}
  };

  // クレデンシャル情報をオブジェクト形式に変換する
  const credentialsJson = JSON.parse(credentials);

  // ドメイン情報を取得する ※envに設定値がない場合は処理を終了する
  const domain = getDomain(apiUrl);
  if (!domain) return {
    headers: {}
  };

  // URLオブジェクトを生成する
  const urlObj = new URL(domain + apiUrl);

  // Sigv4署名を作成する
  const signatureV4 = getSignatureV4(credentialsJson);

  // HTTPリクエストを作成する
  const httpRequest = getHttpRequest(method, urlObj, params);

  // 署名リクエストを作成する
  const signedRequest = await signatureV4.sign(httpRequest);

  // Sigv4署名ヘッダを作成する
  const headers = getSigV4Headers(signedRequest);
  return headers;
};

/**
 * トークンが切れたかどうかを判定する
 *
 * @param {any} error エラーオブジェクト
 * @return トークンが切れたかどうか
 */
const isExpiredToken = (error: any): boolean => {
  return !error.response || !error.response.status || error.response.status === 403;
};
export { isExpiredToken };